import { openDB, DBSchema, IDBPDatabase } from 'idb';
import { supabase } from '@/integrations/supabase/client';

interface OfflineQueueDB extends DBSchema {
  reviews: {
    key: string;
    value: {
      id: string;
      bookId: number;
      userId: string;
      profileId: string;
      content: string;
      rating: number;
      isAnonymous: boolean;
      createdAt: string;
      retryCount: number;
    };
  };
  readingProgress: {
    key: string;
    value: {
      id: string;
      bookId: number;
      userId: string;
      profileId: string;
      currentPage: number;
      timestamp: string;
      retryCount: number;
    };
  };
}

class OfflineQueueManager {
  private db: IDBPDatabase<OfflineQueueDB> | null = null;
  private readonly DB_NAME = 'offline-queue';
  private readonly DB_VERSION = 1;

  async init() {
    if (this.db) return;

    this.db = await openDB<OfflineQueueDB>(this.DB_NAME, this.DB_VERSION, {
      upgrade(db) {
        // Create stores if they don't exist
        if (!db.objectStoreNames.contains('reviews')) {
          db.createObjectStore('reviews', { keyPath: 'id' });
        }
        if (!db.objectStoreNames.contains('readingProgress')) {
          db.createObjectStore('readingProgress', { keyPath: 'id' });
        }
      },
    });
  }

  // Queue a review for offline submission
  async queueReview(data: {
    bookId: number;
    userId: string;
    profileId: string;
    content: string;
    rating: number;
    isAnonymous: boolean;
  }) {
    await this.init();
    if (!this.db) return;

    const id = `review-${data.bookId}-${Date.now()}`;
    await this.db.add('reviews', {
      id,
      ...data,
      createdAt: new Date().toISOString(),
      retryCount: 0,
    });

    // Register background sync if available
    if ('serviceWorker' in navigator && 'sync' in (ServiceWorkerRegistration.prototype as any)) {
      try {
        const registration = await navigator.serviceWorker.ready;
        await (registration as any).sync.register('sync-reviews');
      } catch (error) {
        console.warn('Background Sync registration failed:', error);
      }
    }

    return id;
  }

  // Queue reading progress for offline submission
  async queueReadingProgress(data: {
    bookId: number;
    userId: string;
    profileId: string;
    currentPage: number;
  }) {
    await this.init();
    if (!this.db) return;

    const id = `progress-${data.bookId}-${data.userId}-${data.profileId}`;
    
    // Upsert - replace existing progress for same book/user/profile
    await this.db.put('readingProgress', {
      id,
      ...data,
      timestamp: new Date().toISOString(),
      retryCount: 0,
    });

    // Register background sync if available
    if ('serviceWorker' in navigator && 'sync' in (ServiceWorkerRegistration.prototype as any)) {
      try {
        const registration = await navigator.serviceWorker.ready;
        await (registration as any).sync.register('sync-reading-progress');
      } catch (error) {
        console.warn('Background Sync registration failed:', error);
      }
    }

    return id;
  }

  // Sync all pending reviews
  async syncPendingReviews() {
    await this.init();
    if (!this.db) return { success: 0, failed: 0 };

    const reviews = await this.db.getAll('reviews');
    let success = 0;
    let failed = 0;

    for (const review of reviews) {
      try {
        const { error } = await supabase
          .from('book_reviews')
          .insert({
            user_id: review.userId,
            profile_id: review.profileId,
            book_id: review.bookId,
            content: review.content,
            rating: review.rating,
            is_anonymous: review.isAnonymous,
          });

        if (error) throw error;

        // Remove from queue on success
        await this.db.delete('reviews', review.id);
        success++;
      } catch (error) {
        console.error('Failed to sync review:', error);
        
        // Increment retry count
        review.retryCount++;
        
        // Remove if max retries exceeded (5 attempts)
        if (review.retryCount >= 5) {
          await this.db.delete('reviews', review.id);
        } else {
          await this.db.put('reviews', review);
        }
        
        failed++;
      }
    }

    return { success, failed };
  }

  // Sync all pending reading progress
  async syncPendingProgress() {
    await this.init();
    if (!this.db) return { success: 0, failed: 0 };

    const progressItems = await this.db.getAll('readingProgress');
    let success = 0;
    let failed = 0;

    for (const progress of progressItems) {
      try {
        const { error } = await supabase
          .from('reading_sessions')
          .upsert({
            user_id: progress.userId,
            profile_id: progress.profileId,
            book_id: progress.bookId,
            current_page: progress.currentPage,
            start_time: progress.timestamp,
            end_time: new Date().toISOString(),
          }, {
            onConflict: 'user_id,profile_id,book_id'
          });

        if (error) throw error;

        // Remove from queue on success
        await this.db.delete('readingProgress', progress.id);
        success++;
      } catch (error) {
        console.error('Failed to sync reading progress:', error);
        
        // Increment retry count
        progress.retryCount++;
        
        // Remove if max retries exceeded
        if (progress.retryCount >= 5) {
          await this.db.delete('readingProgress', progress.id);
        } else {
          await this.db.put('readingProgress', progress);
        }
        
        failed++;
      }
    }

    return { success, failed };
  }

  // Get pending counts
  async getPendingCounts() {
    await this.init();
    if (!this.db) return { reviews: 0, progress: 0 };

    const reviews = await this.db.count('reviews');
    const progress = await this.db.count('readingProgress');

    return { reviews, progress };
  }

  // Clear all queues
  async clearAll() {
    await this.init();
    if (!this.db) return;

    await this.db.clear('reviews');
    await this.db.clear('readingProgress');
  }
}

export const offlineQueue = new OfflineQueueManager();
